import React, { useEffect, useRef, useState } from 'react';
import { vendorsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Upload, Download, Trash2, FileText, Loader2 } from 'lucide-react';
import RequireCap from '../auth/RequireCap';

const formatBytes = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

/**
 * Vendor attachments section — used inside VendorViewDialog and VendorEditDialog.
 *
 * Props:
 *   vendorId      — required; the vendor these attachments belong to
 *   readOnly      — hides upload + delete controls (still allows download)
 */
export default function VendorAttachments({ vendorId, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const { data } = await vendorsAPI.listAttachments(vendorId);
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [vendorId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File must be under 25 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await vendorsAPI.uploadAttachment(vendorId, fd);
      toast.success(`Uploaded ${file.name}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Delete attachment "${att.filename}"?`)) return;
    try {
      await vendorsAPI.deleteAttachment(att.id);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const download = (att) => {
    // GET the file with auth via fetch, then blob-download so the browser
    // preserves the filename and honors the Authorization header.
    const token = localStorage.getItem('token');
    fetch(vendorsAPI.downloadAttachmentUrl(att.id), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = att.filename || 'attachment';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Download failed'));
  };

  return (
    <section className="space-y-3" data-testid="vendor-attachments-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">Attachments</p>
        {!readOnly && (
          <RequireCap module="purchase_orders" action="edit">
            <div>
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                onChange={handleUpload}
                data-testid="vendor-attachment-file-input"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                data-testid="vendor-attachment-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          </RequireCap>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-2">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-400 py-2 border border-dashed rounded-lg p-4 text-center" data-testid="vendor-attachments-empty">
          No attachments yet
        </div>
      ) : (
        <ul className="divide-y border rounded-lg">
          {items.map((att) => (
            <li key={att.id} className="flex items-center gap-3 p-3" data-testid={`vendor-attachment-row-${att.id}`}>
              <FileText className="w-5 h-5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{att.filename}</p>
                <p className="text-xs text-slate-500">
                  {formatBytes(att.size)}
                  {att.uploaded_at && ` · ${att.uploaded_at.slice(0, 10)}`}
                  {att.uploaded_by_name && ` · by ${att.uploaded_by_name}`}
                </p>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => download(att)}
                title="Download"
                data-testid={`vendor-attachment-download-${att.id}`}
              >
                <Download className="w-4 h-4" />
              </Button>
              {!readOnly && (
                <RequireCap module="purchase_orders" action="delete">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDelete(att)}
                    title="Delete"
                    data-testid={`vendor-attachment-delete-${att.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </RequireCap>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
